<script setup lang="ts">
import FlatRouterLinkButton from '@/components/atoms/FlatRouterLinkButton.vue'
import BreadcrumbList from '@/components/molecules/BreadcrumbList.vue'
import ChatMessageSendingJobIndexPagePagination from './ChatMessageSendingJobIndexPagePagination.vue'
import ChatMessageSendingJobIndexPageSideMenu from './ChatMessageSendingJobIndexPageSideMenu.vue'
import {
  ChatMessageSendingJobIndexPageDocument,
  type ChatMessageSendingJobIndexPageEdgeFragment,
  type ChatMessageSendingJobIndexPageJobFragment,
  type ChatMessageSendingJobIndexPageJobMessageFragment,
  type ChatMessageSendingJobIndexPagePageInfoFragment,
  type ChatMessageSendingJobIndexPageQuery,
} from './ChatMessageSendingJobIndexPage.generated'
import { useApolloClient } from '@vue/apollo-composable'
import { computed, onMounted, ref } from 'vue'
import { useRoute, RouterLink } from 'vue-router'
import { defineResponseHandlers } from '@/libs/utilities/graphql'
import CenteredPageContainer from '@/components/molecules/CenteredPageContainer.vue'
import ChatMessageSendingJobStatusChip from './ChatMessageSendingJobStatusChip.vue'
import { fromTimeString } from '@/libs/utilities/gql-scalar'
import { format } from 'date-fns'

const route = useRoute('chatMessageSendingJobIndex')

const apolloClient = useApolloClient()

const currentPageNumber = ref(0)

type Page = {
  edges: ChatMessageSendingJobIndexPageEdgeFragment[]
  pageInfo: ChatMessageSendingJobIndexPagePageInfoFragment
}

// GraphQL のカーソルベースのページネーションを、ページ番号形式のように扱う
// Backward pagination が実装されていないこともあり apollo のキャッシュは使わずに、自前の簡易な仕組みでページを管理する
// ブラウザバック時にページネーションを復元したいなど、リッチにしていく場合は仕組みを変更する
const pages = ref<Page[]>([])

const currentPage = computed<Page | undefined>(() => {
  return pages.value[currentPageNumber.value]
})

const fetchPage = async (after: string | undefined) => {
  const res =
    await apolloClient.client.query<ChatMessageSendingJobIndexPageQuery>({
      query: ChatMessageSendingJobIndexPageDocument,
      variables: {
        jobsAfter: after,
      },
      fetchPolicy: 'no-cache',
    })

  return res.data.chatMessageSendingJobs
}

onMounted(async () => {
  const page = await fetchPage(undefined)

  pages.value = [page]
})

const handleRequestNextPage = async () => {
  if (!currentPage.value || !currentPage.value.pageInfo.hasNextPage) {
    throw new Error('Unexpected next page request')
  }

  const nextPage = currentPageNumber.value + 1

  if (pages.value[nextPage]) {
    currentPageNumber.value = nextPage
    return
  }

  if (!currentPage.value.pageInfo.endCursor) {
    throw new Error('Unexpected next page request')
  }

  const page = await fetchPage(currentPage.value.pageInfo.endCursor)

  pages.value = [...pages.value, page]
  currentPageNumber.value = nextPage
}

const handleRequestPreviousPage = async () => {
  const nextPage = currentPageNumber.value - 1

  if (pages.value[nextPage]) {
    currentPageNumber.value = nextPage
    return
  }

  throw new Error('Unexpected previous page request')
}

const stringifyJobTitle = (
  job: ChatMessageSendingJobIndexPageJobFragment
): string => {
  const m = job.messages[0]

  return defineResponseHandlers<
    ChatMessageSendingJobIndexPageJobMessageFragment,
    string
  >({
    ChatMessageSendingJobMessageText: data => data.text.substring(0, 30),
    ChatMessageSendingJobMessageImage: data => data.altText.substring(0, 30),
    ChatMessageSendingJobMessageQuestion: data => data.body.substring(0, 30),
  })(m)
}

const formatNullableTimeString = (
  time: TimeString | null | undefined
): string => {
  if (!time) {
    return '-'
  }

  return format(fromTimeString(time), 'yyyy/MM/dd HH:mm')
}
</script>

<template>
  <CenteredPageContainer :class="$style.page" wide>
    <div :class="[$style.page__main, $style.setting]">
      <ChatMessageSendingJobIndexPageSideMenu />
      <div :class="$style.wrapper">
        <BreadcrumbList :links="route.meta.breadcrumbs" />
        <section :class="$style.container">
          <header :class="$style.header">
            <h1>LINEメッセージ配信</h1>
            <FlatRouterLinkButton
              variant="primary"
              s
              size="medium"
              :to="{ name: 'chatMessageSendingJobNew' }"
              :class="$style.submitButton"
            >
              新規配信
            </FlatRouterLinkButton>
          </header>
          <hr :class="$style.hr" />
          <div :class="$style.containerInner">
            <header :class="$style.paginationHeader">
              <ChatMessageSendingJobIndexPagePagination
                v-if="currentPage"
                :currentPage="currentPageNumber"
                :hasNextPage="currentPage.pageInfo.hasNextPage"
                :hasPreviousPage="currentPageNumber > 0"
                @moveToNextPage="handleRequestNextPage"
                @moveToPreviousPage="handleRequestPreviousPage"
              />
            </header>
            <table :class="$style.table">
              <colgroup>
                <col width="15%" />
                <col />
                <col width="20%" />
                <col width="20%" />
                <col width="20%" />
              </colgroup>
              <thead>
                <tr>
                  <th>配信状況</th>
                  <th>内容</th>
                  <th>ターゲット</th>
                  <th>配信設定日時</th>
                  <th>配信完了日時</th>
                </tr>
              </thead>
              <tbody v-if="currentPage && currentPage.edges.length > 0">
                <tr v-for="edge of currentPage.edges" :key="edge.cursor">
                  <td>
                    <ChatMessageSendingJobStatusChip
                      :status="edge.node.status"
                    />
                  </td>
                  <td>
                    <RouterLink
                      :to="{
                        name: 'chatMessageSendingJobDetail',
                        params: {
                          entranceGUID: route.params.entranceGUID,
                          jobGuid: edge.cursor,
                        },
                      }"
                      >{{ stringifyJobTitle(edge.node) }}</RouterLink
                    >
                  </td>
                  <td>{{ edge.node.target }}</td>
                  <td>
                    {{ formatNullableTimeString(edge.node.scheduledAt) }}
                  </td>
                  <td>
                    {{ formatNullableTimeString(edge.node.sentAt) }}
                  </td>
                </tr>
              </tbody>
              <tbody v-else-if="currentPage?.edges.length === 0">
                <tr>
                  <td colspan="6" :class="$style.none">配信履歴がありません</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  </CenteredPageContainer>
</template>

<style module lang="scss">
.wrapper {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 0 auto;
  width: 100%;
}

.container {
  background-color: $color-surface-primary;
  padding: 16px;
  border-radius: 10px;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.paginationHeader {
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.hr {
  border: none;
  border-top: 1px solid $color-border-low-emphasis;
  margin: 16px 0;
}

.containerInner {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.historyHeading {
  font-size: 16px;
}

.table {
  border-collapse: collapse;

  thead {
    background-color: $color-background-base;

    th {
      padding: 12px;
      text-align: left;
    }
  }

  tbody {
    tr {
      &:not(:first-child) {
        border-top: 1px solid $color-border-low-emphasis;
      }

      td {
        padding: 24px 12px;

        a {
          display: block;
          color: inherit;
          text-decoration: underline;
        }
      }
    }
  }
}

.none {
  text-align: center;
}

.page {
  padding-bottom: 80px;

  &__header {
    &:nth-child(n + 2) {
      margin-top: 40px;
    }
  }

  &__main {
    &:nth-child(n + 2) {
      margin-top: 32px;
    }
  }
}

.setting {
  display: grid;
  align-items: flex-start;
  grid-gap: 40px;
  grid-template-columns: 302px 1fr;
  grid-template-rows: auto;
}
</style>
